import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Text } from '@/design-system/atoms/Text';

import styles from './AudioListItem.module.scss';
import AudioPlayButton from './AudioPlayButton';
import AudioTimeDisplay from './AudioTimeDisplay';
import AudioTimeline from './AudioTimeline';
import AudioVolume from './AudioVolume';

const AudioListItem = ({ label, src, titleTruncation }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(50);
    const audioRef = useRef(null);
    const audioContainerRef = useRef(null);

    // progress bar styles handler
    const progressBarStyleHandler = useCallback(() => {
        const percentageTotal = 100;

        audioContainerRef.current.style.setProperty(
            '--seek-width',
            `${(currentTime / duration) * percentageTotal}%`,
        );
    }, [currentTime, duration]);

    // audio on / off handler
    const handleToggleMuted = useCallback(() => {
        setIsMuted(!isMuted);
    }, [isMuted]);

    const handleTogglePlayPause = () => {
        setIsPlaying((prev) => !prev);
    };

    // audio progress handler
    const handleProgressChange = (event) => {
        const audio = audioRef?.current;
        const newTime = (event.target.value / 100) * duration;
        audio.currentTime = newTime;

        setCurrentTime(newTime);
    };

    // audio volumne handler
    const handleVolumeChange = (e) => {
        setVolume(e.target.value);
    };

    useEffect(
        function initializeAudioPlayer() {
            const audio = audioRef?.current;
            audio.loop = false; // prevent looping
            audio.muted = isMuted; // control audio muted

            const updateCurrentTime = () => {
                setCurrentTime(audio.currentTime);

                // rewind audio
                if (audio.currentTime >= audio.duration && !isSeeking) {
                    audio.pause();
                    audio.currentTime = 0;
                    setIsPlaying(false);
                }
            };

            const audioDataHandler = () => {
                setDuration(audio.duration);
                setCurrentTime(audio.currentTime);
            };

            const seekedHandler = () => {
                setIsSeeking(false);
            };

            const seekingHandler = (event) => {
                setIsSeeking(!!event);
            };

            // event listeners
            audio.addEventListener('timeupdate', updateCurrentTime);
            audio.addEventListener('loadedmetadata', audioDataHandler);
            audio.addEventListener('seeked', seekedHandler);
            audio.addEventListener('seeking', seekingHandler);

            return () => {
                audio.removeEventListener('timeupdate', updateCurrentTime);
                audio.removeEventListener('loadedmetadata', audioDataHandler);
                audio.removeEventListener('seeked', seekedHandler);
                audio.removeEventListener('seeking', seekingHandler);
            };
        },
        [isPlaying, isSeeking, isMuted],
    );

    useEffect(
        function updateAudioPlayer() {
            const audio = audioRef?.current;
            audio.src = src; // Update audio source if it changes
            audio.load();
            setCurrentTime(0); // Reset current time when source changes

            // Cleanup on unmount or when `audioSrc` changes
            return () => {
                audio.pause();
                audio.src = '';
            };
        },
        [src],
    );

    useEffect(
        function volumeController() {
            const audio = audioRef?.current;
            const totalVolume = volume / 100;

            if (audio) {
                audio.volume = totalVolume;
            }

            setIsMuted(totalVolume <= 0);
        },
        [volume],
    );

    useEffect(
        function progressBarHandler() {
            progressBarStyleHandler();
        },
        [progressBarStyleHandler],
    );

    useEffect(
        function audioErrorHandler() {
            if (audioRef.current) {
                // catch error
                if (isPlaying) {
                    audioRef.current.play().catch((error) => {
                        console.error('Failed to play audio:', error);
                    });
                } else {
                    audioRef.current.pause();
                }
            }
        },
        [isPlaying],
    );

    return (
        <div ref={audioContainerRef} className={classnames(styles[`audio__item`])}>
            {label ? (
                <Text
                    tag="span"
                    variant={Text.VARIANT.CTA1}
                    content={label}
                    lineClamp={titleTruncation ? titleTruncation : null}
                    allowInlineHTML
                />
            ) : null}
            <audio className={classnames(styles[`audio__tag`])} src={src} ref={audioRef} />
            <div className={classnames(styles[`audio__controls`])}>
                <AudioPlayButton onTogglePlay={handleTogglePlayPause} isPlaying={isPlaying} />
                <AudioTimeDisplay
                    duration={duration}
                    currentTime={currentTime}
                    titleTruncation={titleTruncation}
                />
                <AudioTimeline
                    duration={duration}
                    currentTime={currentTime}
                    onTimelineChange={handleProgressChange}
                />
                <AudioVolume
                    isMuted={isMuted}
                    volume={volume}
                    onVolumeChange={handleVolumeChange}
                    onToggleMuted={handleToggleMuted}
                />
            </div>
        </div>
    );
};

AudioListItem.propTypes = {
    /**
     * Required label for the audio list item
     */
    label: PropTypes.string.isRequired,
    /**
     * Required src for the audio list item
     */
    src: PropTypes.string.isRequired,
    /**
     * Optional title truncation for the link list item
     */
    titleTruncation: PropTypes.shape({ sm: PropTypes.number, lg: PropTypes.number }),
};

export default AudioListItem;
